import React, { useState } from 'react';
import dayjs from 'dayjs';

type Props = {
  price: number | string;
};

export default function InBankModal({ price }: Props) {
  const numericPrice = Number(price);
  const [months, setMonths] = useState(3);
  const installment = (numericPrice / months).toFixed(2);
  const minPrice = months === 1 ? 30 : months === 3 ? 100 : 175;
  const maxPrice = months === 1 ? 800 : 2500;
  const schedule = Array.from({ length: months }, (_, index) => ({
    number: index + 1,
    isoDate: dayjs()
      .add(index + 1, 'month')
      .format('YYYY-MM-DD'),
    date: dayjs()
      .add(index + 1, 'month')
      .format('DD.MM.YYYY'),
  }));

  return (
    <div className="mx-auto box-border w-full max-w-560 px-4 pb-5 sm:px-6 sm:pb-6">
      <div className="mb-5">
        <p className="text-sm leading-6 text-rgrey">
          Inbanki saad kasutada mugavalt Rademari e-poes ja kauplustes.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-sm text-rgrey">Ostukorvi summa</span>
          <span className="whitespace-nowrap text-xl font-axi-bold text-rgreen">
            {numericPrice.toFixed(2)} €
          </span>
        </div>

        <fieldset className="mt-4">
          <legend className="text-sm font-axi-bold">Vali makseviis</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-3">
            {[1, 3, 6].map((period) => {
              const selected = months === period;
              const optionLabel =
                period === 1 ? 'Järgmisel kuul' : `${period} kuud`;
              const optionAmount = (numericPrice / period).toFixed(2);

              return (
                <label key={period} className="cursor-pointer">
                  <input
                    className="peer sr-only"
                    type="radio"
                    name="inbank-payment-period"
                    value={period}
                    checked={selected}
                    onChange={() => setMonths(period)}
                  />
                  <span
                    className={`flex h-full flex-col justify-center rounded-md border px-3 py-2.5 transition-colors motion-reduce:transition-none peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-rgreen ${
                      selected
                        ? 'border-rgreen bg-rgreen/5 text-black'
                        : 'border-gray-300 bg-white text-black hover:border-rgreen'
                    }`}
                  >
                    <span className="text-sm font-axi-bold">{optionLabel}</span>
                    <span className="mt-1 text-xs text-rgrey">
                      {optionAmount} €
                      {period === 1 ? ' ühe maksena' : ' / osamakse'}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            Valitud makseviis:{' '}
            {months === 1 ? 'maksa järgmisel kuul' : `maksa ${months} osas`}.
            Osamakse summa {installment} eurot.
          </p>
        </fieldset>

        <div className="mt-5">
          <h3 className="text-sm font-axi-bold">Maksegraafik</h3>
          <ol
            className="mt-2 space-y-2"
            aria-label={
              months === 1 ? 'Ühekordne makse' : `${months} osamakset`
            }
          >
            {schedule.map(({ number, date, isoDate }) => (
              <li
                key={number}
                className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rgreen/10 text-xs font-axi-bold text-rgreen"
                  >
                    {number}
                  </span>
                  <div>
                    <p className="text-sm font-axi-bold">
                      {months === 1 ? 'Ühekordne makse' : `${number}. osamakse`}
                    </p>
                    <time className="text-xs text-rgrey" dateTime={isoDate}>
                      {date}
                    </time>
                  </div>
                </div>
                <span className="whitespace-nowrap text-sm font-axi-bold">
                  {installment} €
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-gray-200 bg-white p-4 text-sm leading-5">
        <p className="font-axi-bold">
          {months === 1 ? 'Maksa järgmisel kuul' : `Maksa ${months} osas`}
        </p>
        <ul className="mt-2 space-y-1 text-rgrey text-xs list-disc mx-4">
          <li>Esimene makse alles kuu aja pärast.</li>
          <li>Ei ühtegi lisatasu.</li>
          <li>
            Kehtib ostusummale {minPrice} – {maxPrice} €.
          </li>
        </ul>
      </div>

      <p className="mt-3 text-xs leading-5 text-rgrey">
        <span className="font-axi-bold">NB!</span> Osamaksu kalkulaatori
        kuupäevad ja summad on informatiivse iseloomuga.
      </p>
    </div>
  );
}
